﻿import {
  AlertCircle,
  ArrowLeft,
  Ban,
  CheckCircle2,
  Loader2,
  Mail,
  MessageSquare,
  RefreshCcw,
  Ticket,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import type { OrderDetailResponse, OrderStatus } from "@ticketnjoy/types";

import { DashboardPage } from "@/components/dashboard-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  useCancelOrderMutation,
  useOrderQuery,
  useRefundOrderMutation,
  useResendEmailMutation,
  useResendSmsMutation,
} from "@/features/orders/api";
import { ApiError } from "@/lib/api-client";

const STATUS_LABELS: Record<OrderStatus, string> = {
  Pending: "Ödeme Bekliyor",
  Paid: "Ödendi",
  Cancelled: "İptal Edildi",
  Expired: "Süresi Doldu",
  Refunded: "İade Edildi",
};

const STATUS_VARIANTS: Record<
  OrderStatus,
  "success" | "warning" | "secondary" | "destructive" | "outline" | "default"
> = {
  Pending: "warning",
  Paid: "success",
  Cancelled: "destructive",
  Expired: "secondary",
  Refunded: "outline",
};

export function OrderDetailPage() {
  return (
    <DashboardPage>
      <OrderDetailView />
    </DashboardPage>
  );
}

function OrderDetailView() {
  const params = useParams<{ orderNumber: string }>();
  const orderNumber = params.orderNumber ?? "";
  const { data, isLoading, error } = useOrderQuery(orderNumber);

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/orders"
          className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Siparişlere dön
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-mono text-2xl font-semibold tracking-tight">
            {orderNumber}
          </h1>
          {data && (
            <Badge variant={STATUS_VARIANTS[data.status]}>
              {STATUS_LABELS[data.status]}
            </Badge>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          Sipariş detayı yüklenemedi.
        </div>
      ) : data ? (
        <OrderDetailContent order={data} />
      ) : null}
    </div>
  );
}

function OrderDetailContent({ order }: { order: OrderDetailResponse }) {
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Alıcı Bilgileri</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
            <Field label="Ad Soyad">
              {order.buyerFirstName} {order.buyerLastName}
            </Field>
            <Field label="E-posta">{order.buyerEmail}</Field>
            <Field label="Telefon">{order.buyerPhone || "—"}</Field>
            <Field label="Etkinlik">
              <div>{order.eventTitle}</div>
              <Link
                to={`/events/${order.eventSlug}`}
                className="text-xs text-muted-foreground hover:underline"
              >
                Etkinliği aç →
              </Link>
            </Field>
            <Field label="Oluşturuldu">{formatDate(order.createdAt)}</Field>
            {order.paidAt && (
              <Field label="Ödendi">{formatDate(order.paidAt)}</Field>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Sipariş Kalemleri</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Bilet Tipi</th>
                    <th className="px-4 py-2 text-right font-medium">Adet</th>
                    <th className="px-4 py-2 text-right font-medium">Birim</th>
                    <th className="px-4 py-2 text-right font-medium">Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((it) => (
                    <tr key={it.id} className="border-t">
                      <td className="px-4 py-2">
                        {it.ticketTypeName}
                        {it.isFree && (
                          <Badge variant="secondary" className="ml-2">
                            Bedava
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {it.quantity}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {formatPrice(it.unitPrice, order.currency)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium">
                        {formatPrice(it.subtotal, order.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {order.tickets.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Ticket className="h-4 w-4" />
                Biletler ({order.tickets.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">Kod</th>
                      <th className="px-4 py-2 text-left font-medium">Tip</th>
                      <th className="px-4 py-2 text-left font-medium">Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.tickets.map((tk) => (
                      <tr key={tk.id} className="border-t">
                        <td className="px-4 py-2 font-mono text-xs">
                          {tk.ticketCode}
                        </td>
                        <td className="px-4 py-2">{tk.ticketTypeName}</td>
                        <td className="px-4 py-2">
                          <Badge
                            variant={
                              tk.status === "Valid"
                                ? "success"
                                : tk.status === "Used"
                                ? "secondary"
                                : "destructive"
                            }
                          >
                            {tk.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {order.lastPaymentError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            <div className="font-medium">Son ödeme hatası</div>
            <div className="mt-1 text-xs">{order.lastPaymentError}</div>
          </div>
        )}
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Fiyat Özeti</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            <Row
              label="Ara toplam"
              value={formatPrice(order.subtotal, order.currency)}
            />
            {order.discountTotal > 0 && (
              <Row
                label="İndirim"
                value={`−${formatPrice(order.discountTotal, order.currency)}`}
              />
            )}
            {order.taxTotal > 0 && (
              <Row label="KDV" value={formatPrice(order.taxTotal, order.currency)} />
            )}
            {order.serviceFeeTotal > 0 && (
              <Row
                label="Servis bedeli"
                value={formatPrice(order.serviceFeeTotal, order.currency)}
              />
            )}
            <div className="border-t pt-2" />
            <Row
              label="Toplam"
              value={formatPrice(order.total, order.currency)}
              bold
            />
          </CardContent>
        </Card>

        <OrderActions order={order} />
      </div>
    </div>
  );
}

/**
 * Durum-bazlı aksiyon paneli:
 * - Pending → İptal Et (opsiyonel sebep)
 * - Paid    → İade Et, E-posta Gönder, SMS Gönder
 * - Refunded/Cancelled/Expired → terminal durum, aksiyon yok
 */
function OrderActions({ order }: { order: OrderDetailResponse }) {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState("");

  const cancelMut = useCancelOrderMutation(order.orderNumber);
  const refundMut = useRefundOrderMutation(order.orderNumber);
  const emailMut = useResendEmailMutation(order.orderNumber);
  const smsMut = useResendSmsMutation(order.orderNumber);

  async function handleCancel() {
    try {
      await cancelMut.mutateAsync({ reason: reason.trim() || undefined });
      toast.success("Sipariş iptal edildi.");
      setCancelOpen(false);
      setReason("");
    } catch (e) {
      toast.error(getErrorMessage(e, "Sipariş iptal edilemedi."));
    }
  }

  async function handleRefund() {
    if (!confirm("Bu siparişi iade etmek istediğine emin misin? Ödeme sağlayıcısına gerçek iade çağrısı yapılacak.")) return;
    try {
      await refundMut.mutateAsync();
      toast.success("İade başarıyla tamamlandı.");
    } catch (e) {
      toast.error(getErrorMessage(e, "İade yapılamadı."));
    }
  }

  async function handleResendEmail() {
    try {
      await emailMut.mutateAsync();
      toast.success("Onay e-postası tekrar gönderildi.");
    } catch (e) {
      toast.error(getErrorMessage(e, "E-posta gönderilemedi."));
    }
  }

  async function handleResendSms() {
    try {
      await smsMut.mutateAsync();
      toast.success("Onay SMS'i tekrar gönderildi.");
    } catch (e) {
      toast.error(getErrorMessage(e, "SMS gönderilemedi."));
    }
  }

  if (order.status === "Pending") {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">İşlemler</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!cancelOpen ? (
            <Button
              variant="destructive"
              className="w-full"
              onClick={() => setCancelOpen(true)}
            >
              <Ban className="h-4 w-4" />
              Siparişi İptal Et
            </Button>
          ) : (
            <div className="space-y-2">
              <label className="block text-sm font-medium">
                İptal sebebi (opsiyonel)
              </label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Örn. Ödeme alınamadı — manuel iptal"
                maxLength={500}
              />
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleCancel}
                  disabled={cancelMut.isPending}
                >
                  {cancelMut.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Ban className="h-4 w-4" />
                  )}
                  İptal Et
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setCancelOpen(false);
                    setReason("");
                  }}
                  disabled={cancelMut.isPending}
                >
                  Vazgeç
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  if (order.status === "Paid") {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">İşlemler</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={handleResendEmail}
            disabled={emailMut.isPending}
          >
            {emailMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
            Onay E-postasını Tekrar Gönder
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={handleResendSms}
            disabled={smsMut.isPending || !order.buyerPhone}
            title={!order.buyerPhone ? "Telefon bilgisi yok" : undefined}
          >
            {smsMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MessageSquare className="h-4 w-4" />
            )}
            Onay SMS'ini Tekrar Gönder
          </Button>
          <div className="pt-1" />
          <Button
            variant="destructive"
            className="w-full justify-start"
            onClick={handleRefund}
            disabled={refundMut.isPending}
          >
            {refundMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCcw className="h-4 w-4" />
            )}
            İade Et
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Terminal durumlar: Refunded / Cancelled / Expired
  const terminalLabel =
    order.status === "Refunded"
      ? "Sipariş iade edildi — aksiyon yapılamaz."
      : order.status === "Cancelled"
      ? "Sipariş iptal edildi — aksiyon yapılamaz."
      : "Süresi doldu — aksiyon yapılamaz.";

  return (
    <Card>
      <CardContent className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <CheckCircle2 className="h-4 w-4" />
        {terminalLabel}
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={
          bold ? "tabular-nums text-base font-semibold" : "tabular-nums"
        }
      >
        {value}
      </span>
    </div>
  );
}

function getErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.detail || fallback;
  if (e instanceof Error) return e.message || fallback;
  return fallback;
}

const PRICE_FMT = new Intl.NumberFormat("tr-TR", {
  style: "decimal",
  maximumFractionDigits: 2,
});

function formatPrice(value: number, currency: string) {
  return `${PRICE_FMT.format(value)} ${currency}`;
}

const DATE_FMT = new Intl.DateTimeFormat("tr-TR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Istanbul",
});

function formatDate(iso: string) {
  return DATE_FMT.format(new Date(iso));
}
